import React, {
  useCallback, useMemo, useState, useEffect, useContext, useRef
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input } from '@glu/form-components';
import { FormContext } from '@glu/validation-react';
import { withStyles } from '@glu/theming';

import ContrastCheck from '../ContrastCheck/ContrastCheck';
import useDebounce from '../../utility/useDebounce';
import getColorValue from '../../utility/getColorValue';

import styles from './Color.styles';

const Color = ({
  classes,
  name,
  labelText,
  backgroundColor,
  fontSize,
  fontWeight,
  className,
  onChange: passedOnChange,
  value: passedValue,
  onClick,
  ...props
}) => {
  const {
    data,
    onChange: contextOnChange
  } = useContext(FormContext);
  const onChange = passedOnChange || contextOnChange;
  const value = passedValue || data[name] || '';
  const [localColor, setLocalColor] = useState(value);
  const [localValue, setLocalValue] = useState(value);
  /** used to prevent the component from calling onChange on the initial render */
  const isMounted = useRef(false);
  const debouncedColor = useDebounce(localColor, 500);

  /** set the value locally */
  const onChangeColor = useCallback((e) => {
    setLocalColor(e.target.value);
  }, [setLocalColor]);

  /** Gather values */
  const backgroundColorValue = useMemo(() => data[backgroundColor] || backgroundColor,
    [backgroundColor, data]);
  const fontSizeValue = useMemo(() => data[fontSize] || fontSize, [fontSize, data]);
  const fontWeightValue = useMemo(() => data[fontWeight] || fontWeight, [fontWeight, data]);

  useEffect(() => {
    if ((localValue.length === 4 || localValue.length === 7) && isMounted.current) {
      onChange(name, localValue);
      setLocalColor(localValue);
    }
  }, [localValue, onChange, name]);

  useEffect(() => {
    if (isMounted.current) {
      onChange(name, debouncedColor);
    } else {
      isMounted.current = true;
    }
  }, [debouncedColor, onChange, name]);

  useEffect(() => {
    // Update local state if value changed externally
    setLocalColor(value);
    setLocalValue(value);
  }, [value]);

  return (
    <div className={classnames(classes.root, className)}>
      <Input
        name={name}
        labelText={labelText}
        value={localValue}
        disableInputAPI
        onChange={({ target: { value: newValue } }) => {
          const hashPrefixVal = `#${newValue.replace(/\W/g, '').slice(0, 6)}`;
          setLocalValue(hashPrefixVal);
        }}
        onBlur={() => {
          if (localValue.length !== 4 && localValue.length !== 7) {
            setLocalValue(localColor);
          }
        }}
        onClick={onClick}
        {...props}
      />
      <input
        type="color"
        name={name}
        value={getColorValue(localColor) || '#ffffff'}
        onChange={onChangeColor}
        onClick={onClick}
      />
      <ContrastCheck
        color={getColorValue(value)}
        backgroundColor={getColorValue(backgroundColorValue)}
        fontSize={fontSizeValue}
        fontWeight={fontWeightValue}
      />
    </div>
  );
};

Color.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  name: PropTypes.string.isRequired,
  labelText: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  value: PropTypes.string
};

Color.defaultProps = {
  backgroundColor: null,
  fontSize: null,
  fontWeight: null,
  className: undefined,
  onChange: undefined,
  onClick: undefined,
  value: undefined
};

export default withStyles(styles)(Color);
